// src/screens/EventsOrganizers.jsx — Organizers + Halls CRUD + wallet/transactions/revenue-share
import "bootstrap-icons/font/bootstrap-icons.css";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";

const LOGS_API = (process.env.REACT_APP_ADMIN_LOGS_API_URL || "").trim();
function postLog(description) {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = user?.user_name || user?.name || "Admin";
    const token = localStorage.getItem("access_token") || "";
    fetch(LOGS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ description, user: name, admin_name: name, timestamp: new Date().toISOString() }),
    }).catch(() => {});
  } catch { }
}

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token");
  if (t) return String(t).trim();
  try { const u=JSON.parse(localStorage.getItem("user")||"{}"); return String(u?.access_token||u?.token||"").trim(); } catch { return ""; }
}
function getAuthHeaders(json=false) {
  const h={Accept:"application/json"}; const t=getBearerToken();
  if (t) h.Authorization=`Bearer ${t}`;
  if (json) h["Content-Type"]="application/json";
  return h;
}

async function apiCall(method, path, body) {
  if (!EVENTS_BASE) throw new Error("Events API URL not configured.");
  const opts = { method, headers:getAuthHeaders(!!body), cache:"no-store" };
  if (body) opts.body=JSON.stringify(body);
  const res  = await fetch(`${EVENTS_BASE}${path}`, opts);
  const json = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
  return json;
}

const fmt = (n) => `BTN. ${Number(n||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

export default function EventsOrganizers() {
  useIdleLogout();
  const navigate = useNavigate();

  const [tab, setTab]           = useState("organizers");
  const [organizers, setOrgs]   = useState([]);
  const [halls, setHalls]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState("");
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* ---- Organizers ---- */
  const fetchOrgs = useCallback(async () => {
    if (!EVENTS_BASE) { setErr("Events API URL not configured."); return; }
    try {
      setLoading(true); setErr("");
      const res  = await fetch(`${EVENTS_BASE}/organizers`, { headers:getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
      setOrgs(Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []);
    } catch(e) { setErr(e.message||"Failed to load organizers."); }
    finally { setLoading(false); }
  }, []);

  /* ---- Halls ---- */
  const fetchHalls = useCallback(async () => {
    if (!EVENTS_BASE) return;
    try {
      setLoading(true); setErr("");
      const res  = await fetch(`${EVENTS_BASE}/halls`, { headers:getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
      setHalls(Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []);
    } catch(e) { setErr(e.message||"Failed to load halls."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrgs(); fetchHalls(); }, [fetchOrgs, fetchHalls]);

  /* ---- View Wallet ---- */
  const handleViewWallet = async (org) => {
    try {
      const json = await apiCall("GET", `/organizers/${org.id}/wallet`);
      const w = json?.data?.wallet || {};
      Swal.fire({
        title: `Wallet — ${org.name}`,
        confirmButtonText: "Close",
        html: `<div class="text-start" style="font-size:13px">
          <div class="row g-2">
            <div class="col-6"><strong>Wallet ID:</strong><br/><code>${w.wallet_id||"—"}</code></div>
            <div class="col-6"><strong>Status:</strong><br/>
              <span class="badge bg-${w.status==="ACTIVE"?"success":"secondary"}">${w.status||"—"}</span>
            </div>
            <div class="col-6"><strong>Balance:</strong><br/><span class="fw-bold fs-5">${fmt(w.amount)}</span></div>
            <div class="col-6"><strong>User ID:</strong><br/>${w.user_id||"—"}</div>
            <div class="col-6"><strong>Created:</strong><br/>${w.created_at||"—"}</div>
            <div class="col-6"><strong>Updated:</strong><br/>${w.updated_at||"—"}</div>
          </div>
        </div>`,
      });
    } catch(e) { Swal.fire({icon:"error",title:"Failed",text:e.message}); }
  };

  /* ---- View / Edit Revenue Share ---- */
  const handleRevenueShare = async (org) => {
    let share = null;
    try {
      const json = await apiCall("GET", `/organizers/${org.id}/revenue-share`);
      share = json?.data?.share || null;
    } catch(e) { Swal.fire({icon:"error",title:"Failed to load revenue share",text:e.message}); return; }

    const currentPct = share ? Number(share.org_share_pct) : 80;
    const { value: newPct, isConfirmed } = await Swal.fire({
      title: `Revenue Share — ${org.name}`,
      showCancelButton: true,
      confirmButtonText: "Save",
      html: `<div class="text-start" style="font-size:13px">
        ${share ? `
          <div class="row g-2 mb-3">
            <div class="col-6"><strong>Total Revenue:</strong><br/>${fmt(share.total_revenue)}</div>
            <div class="col-6"><strong>Organizer Total:</strong><br/>${fmt(share.total_org_revenue)}</div>
            <div class="col-6"><strong>Platform Total:</strong><br/>${fmt(share.total_tabdey_revenue)}</div>
            <div class="col-6"><strong>Last Updated:</strong><br/><span style="font-size:11px">${share.updated_at ? new Date(share.updated_at).toLocaleString() : "—"}</span></div>
          </div>` : `<p class="text-muted small mb-3">No share config set yet. Bookings default to 80/20 until saved.</p>`}
        <label class="form-label fw-semibold">Organizer Share % (0–100) *</label>
        <div class="input-group">
          <input id="rs-pct" type="number" min="0" max="100" step="0.01" class="form-control" value="${currentPct}">
          <span class="input-group-text">%</span>
        </div>
        <div class="text-muted mt-1" style="font-size:11px">Platform (Tabdey) gets the remaining %.</div>
      </div>`,
      preConfirm: () => {
        const v = Number(document.getElementById("rs-pct").value);
        if (isNaN(v) || v < 0 || v > 100) { Swal.showValidationMessage("Enter a value between 0 and 100"); return false; }
        return v;
      },
    });
    if (!isConfirmed) return;
    try {
      await apiCall("PUT", `/organizers/${org.id}/revenue-share`, { org_share_pct: newPct });
      postLog(`Updated revenue share for "${org.name}" to ${newPct}%`);
      Swal.fire({ icon:"success", title:"Revenue share updated!", timer:1400, showConfirmButton:false });
    } catch(e) { Swal.fire({icon:"error",title:"Failed",text:e.message}); }
  };

  /* ---- Delete Organizer ---- */
  const handleDeleteOrg = async (org) => {
    const { isConfirmed } = await Swal.fire({
      icon:"warning", title:`Delete "${org.name}"?`,
      text:"This will remove the organizer and their linked user account.",
      showCancelButton:true, confirmButtonColor:"#dc3545", confirmButtonText:"Yes, Delete",
    });
    if (!isConfirmed) return;
    try {
      setDeletingId(org.id);
      await apiCall("DELETE", `/organizers/${org.id}`);
      postLog(`Deleted organizer "${org.name}"`);
      Swal.fire({ icon:"success", title:"Deleted!", timer:1200, showConfirmButton:false });
      fetchOrgs();
    } catch(e) {
      if (/cannot remove organizer|event/i.test(e.message)) {
        Swal.fire({
          icon:"error", title:"Cannot Delete",
          html:`<p>${e.message}</p><p class="text-muted small mb-0">Delete or reassign their events first, then try again.</p>`,
        });
      } else {
        Swal.fire({icon:"error",title:"Failed",text:e.message});
      }
    } finally { setDeletingId(null); }
  };

  /* ---- Create Organizer ---- */
  const handleCreateOrg = async () => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title: "Add Organizer", width: "520px", showCancelButton: true, confirmButtonText: "Create",
      html: `<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-12"><label class="form-label fw-semibold">Name *</label>
          <input id="org-name" class="form-control form-control-sm" placeholder="e.g. New Edge Events"></div>
        <div class="col-12"><label class="form-label fw-semibold">Email *</label>
          <input id="org-email" type="email" class="form-control form-control-sm" placeholder="contact@example.com"></div>
        <div class="col-12"><label class="form-label fw-semibold">Phone *</label>
          <input id="org-phone" class="form-control form-control-sm" placeholder="+975XXXXXXXX"></div>
      </div>`,
      preConfirm: () => {
        const name  = document.getElementById("org-name").value.trim();
        const email = document.getElementById("org-email").value.trim();
        const phone = document.getElementById("org-phone").value.trim();
        if (!name)  { Swal.showValidationMessage("Name is required"); return false; }
        if (name.length < 2) { Swal.showValidationMessage("Name must be at least 2 characters"); return false; }
        if (!email) { Swal.showValidationMessage("Email is required"); return false; }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { Swal.showValidationMessage("Enter a valid email address"); return false; }
        if (!phone) { Swal.showValidationMessage("Phone is required"); return false; }
        if (!/^\+?[0-9\s-]{7,15}$/.test(phone)) { Swal.showValidationMessage("Enter a valid phone number (7–15 digits)"); return false; }
        return { name, email, phone };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId("new-org");
      const res = await apiCall("POST", "/organizers", fd);
      const d = res?.data || {};
      postLog(`Created organizer "${fd.name}"`);
      Swal.fire({
        icon:"success", title:"Organizer Created!",
        html: `<div class="text-start" style="font-size:13px">
          <div class="row g-2">
            <div class="col-6"><strong>Name:</strong><br/>${d.name||fd.name}</div>
            <div class="col-6"><strong>Wallet ID:</strong><br/><code>${d.wallet_id||"—"}</code></div>
            <div class="col-6"><strong>Email:</strong><br/>${d.email||fd.email}</div>
            <div class="col-6"><strong>Phone:</strong><br/>${d.phone||fd.phone}</div>
            ${d.default_password ? `<div class="col-12 mt-1"><strong>Default Password:</strong><br/><code>${d.default_password}</code> <span class="text-danger small">(share securely)</span></div>` : ""}
          </div>
        </div>`,
      });
      fetchOrgs();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* ---- Create Hall ---- */
  const handleCreateHall = async () => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title:"Add Hall", width:"560px", showCancelButton:true, confirmButtonText:"Create",
      html:`<div class="row g-2 text-start" style="font-size:14px">
        <div class="col-6"><label class="form-label fw-semibold">Venue Name *</label><input id="h-venue" class="form-control form-control-sm" placeholder="e.g. Lugar Cinema"></div>
        <div class="col-6"><label class="form-label fw-semibold">Hall Name *</label><input id="h-name" class="form-control form-control-sm" placeholder="e.g. Hall I"></div>
        <div class="col-6"><label class="form-label fw-semibold">Total Seats *</label><input id="h-seats" type="number" min="1" class="form-control form-control-sm"></div>
        <div class="col-6 d-flex align-items-end pb-1"><div class="form-check"><input type="checkbox" id="h-balcony" class="form-check-input"><label class="form-check-label" for="h-balcony">Has Balcony</label></div></div>
      </div>`,
      preConfirm: () => {
        const venue_name  = document.getElementById("h-venue").value.trim();
        const name        = document.getElementById("h-name").value.trim();
        const total_seats = Number(document.getElementById("h-seats").value);
        if (!venue_name)  { Swal.showValidationMessage("Venue name is required"); return false; }
        if (!name)        { Swal.showValidationMessage("Hall name is required"); return false; }
        if (!total_seats) { Swal.showValidationMessage("Total seats is required"); return false; }
        return { venue_name, name, total_seats, has_balcony: document.getElementById("h-balcony").checked };
      },
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId("new-hall");
      await apiCall("POST", "/halls", fd);
      postLog(`Created hall "${fd.name}" at "${fd.venue_name}" (${fd.total_seats} seats)`);
      Swal.fire({ icon:"success", title:"Hall created!", timer:1200, showConfirmButton:false });
      fetchHalls();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* ---- Add Seats to Hall ---- */
  const handleAddSeats = async (hallId) => {
    const { value: rawSeats, isConfirmed } = await Swal.fire({
      title:"Add Seats", showCancelButton:true, confirmButtonText:"Add Seats", width:"600px",
      html:`<div class="text-start" style="font-size:13px">
        <p class="text-muted mb-2">Enter seats as JSON array. Each seat: <code>{"row_label","seat_number","column_position","section","category"}</code></p>
        <p class="small text-muted">section: main | balcony &nbsp;·&nbsp; category: regular | premium | balcony</p>
        <textarea id="seats-json" class="form-control font-monospace" rows="6" placeholder='[{"row_label":"A","seat_number":1,"column_position":1,"section":"main","category":"regular"}]'></textarea>
      </div>`,
      preConfirm: () => {
        try {
          const val = document.getElementById("seats-json").value.trim();
          const parsed = JSON.parse(val);
          if (!Array.isArray(parsed) || !parsed.length) { Swal.showValidationMessage("Enter a valid JSON array of seats"); return false; }
          return parsed;
        } catch { Swal.showValidationMessage("Invalid JSON — please check your input"); return false; }
      },
    });
    if (!isConfirmed || !rawSeats) return;
    try {
      const json = await apiCall("POST", `/halls/${hallId}/seats`, { seats: rawSeats });
      postLog(`Added ${rawSeats.length} seat(s) to hall (ID: ${hallId})`);
      Swal.fire({ icon:"success", title:"Seats added!", text: json?.message || `${rawSeats.length} seat(s) added.`, timer:2000, showConfirmButton:false });
      fetchHalls();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
  };

  return (
    <>
      {err && <div className="alert alert-danger">{err}</div>}

      {/* METRICS */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 events-metrics">
        {[
          { label: "Total Organizers", value: loading ? "…" : organizers.length },
          { label: "Total Events",     value: loading ? "…" : organizers.reduce((s,o)=>s+Number(o.event_count||0),0).toLocaleString() },
          { label: "Total Halls",      value: loading ? "…" : halls.length },
          { label: "Total Seats",      value: loading ? "…" : halls.reduce((s,h)=>s+Number(h.total_seats||0),0).toLocaleString() },
        ].map((m,i)=>(
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* TABS */}
      <div className="card mb-3 search-filter-card">
        <div className="card-body searchbar-row-inline" style={{padding:"10px 14px"}}>
          <div className="d-flex gap-2">
            <button
              className={`btn ${tab==="organizers" ? "btn-primary" : "btn-outline-secondary"}`}
              style={{borderRadius:999,fontWeight:600,fontSize:14,padding:"6px 22px"}}
              onClick={()=>setTab("organizers")}
            >
              <i className="bi bi-people me-1"/>Organizers
            </button>
            <button
              className={`btn ${tab==="halls" ? "btn-primary" : "btn-outline-secondary"}`}
              style={{borderRadius:999,fontWeight:600,fontSize:14,padding:"6px 22px"}}
              onClick={()=>setTab("halls")}
            >
              <i className="bi bi-building me-1"/>Halls &amp; Seats
            </button>
          </div>
        </div>
      </div>

      {/* ===== ORGANIZERS ===== */}
      {tab==="organizers" && (
        <div className="bookings-table-card card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Organizers</h5>
            <button className="ev-header-btn" onClick={handleCreateOrg} disabled={savingId==="new-org"}>
              <i className="bi bi-plus-lg"/>Add Organizer
            </button>
          </div>
          {loading && <div className="text-center py-5"><span className="spinner-border"/></div>}
          {!loading && (
            <div className="table-responsive">
              <table className="table table-hover align-middle table-pro">
                <colgroup>
                  <col style={{width:"50px"}}/><col style={{width:"200px"}}/><col style={{width:"120px"}}/>
                  <col style={{width:"160px"}}/><col style={{width:"110px"}}/><col style={{width:"60px"}}/>
                  <col style={{width:"110px"}}/><col style={{width:"210px"}}/>
                </colgroup>
                <thead>
                  <tr><th>SI.NO</th><th>Name</th><th>Wallet ID</th><th>Email</th><th>Phone</th><th>Events</th><th>Created At</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {organizers.length===0 && <tr><td colSpan={8} className="text-center py-4 text-muted">No organizers yet.</td></tr>}
                  {organizers.map((o,i)=>(
                    <tr key={o.id}>
                      <td>{i+1}</td>
                      <td className="fw-semibold">{o.name||"—"}</td>
                      <td className="mono" style={{fontSize:12}}>{o.user?.wallet_id||"—"}</td>
                      <td style={{fontSize:12}}>{o.user?.email||"—"}</td>
                      <td style={{fontSize:13}}>{o.user?.phone||"—"}</td>
                      <td className="mono">{o.event_count??0}</td>
                      <td style={{fontSize:12}}>{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                      <td>
                        <div className="ev-actions">
                          <button className="ev-btn ev-btn-manage" title="Revenue"
                            onClick={()=>navigate(`/services/events/revenue/organizer/${o.id}?tab=revenue`,{state:{orgName:o.name}})}>
                            <i className="bi bi-bar-chart"/>
                          </button>
                          <button className="ev-btn ev-btn-edit" title="Wallet" onClick={()=>handleViewWallet(o)}>
                            <i className="bi bi-wallet2"/>
                          </button>
                          <button className="ev-btn ev-btn-toggle" title="Transactions"
                            onClick={()=>navigate(`/services/events/revenue/organizer/${o.id}?tab=transactions`,{state:{orgName:o.name}})}>
                            <i className="bi bi-clock-history"/>
                          </button>
                          <button className="ev-btn ev-btn-live" title="Revenue Share" onClick={()=>handleRevenueShare(o)}>
                            <i className="bi bi-percent"/>
                          </button>
                          <button className="ev-btn ev-btn-delete" title="Delete" disabled={deletingId===o.id} onClick={()=>handleDeleteOrg(o)}>
                            {deletingId===o.id ? <span className="spinner-border spinner-border-sm"/> : <i className="bi bi-trash"/>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===== HALLS & SEATS ===== */}
      {tab==="halls" && (
        <div className="bookings-table-card card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Halls &amp; Seats</h5>
            <button className="ev-header-btn" onClick={handleCreateHall} disabled={savingId==="new-hall"}>
              <i className="bi bi-plus-lg"/>Add Hall
            </button>
          </div>
          {loading && <div className="text-center py-5"><span className="spinner-border"/></div>}
          {!loading && (
            <div className="table-responsive">
              <table className="table table-hover align-middle table-pro">
                <colgroup>
                  <col style={{width:"60px"}}/><col style={{width:"200px"}}/><col style={{width:"160px"}}/>
                  <col style={{width:"100px"}}/><col style={{width:"100px"}}/><col style={{width:"160px"}}/>
                </colgroup>
                <thead>
                  <tr><th>SI.NO</th><th>Venue Name</th><th>Hall Name</th><th>Total Seats</th><th>Has Balcony</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {halls.length===0 && <tr><td colSpan={6} className="text-center py-4 text-muted">No halls yet.</td></tr>}
                  {halls.map((h,i)=>(
                    <tr key={h.id}>
                      <td>{i+1}</td>
                      <td>{h.venue_name||"—"}</td>
                      <td className="fw-semibold">{h.name||"—"}</td>
                      <td className="mono">{h.total_seats||0}</td>
                      <td>{h.has_balcony ? <span className="badge bg-success">Yes</span> : <span className="badge bg-secondary">No</span>}</td>
                      <td>
                        <div className="ev-actions">
                          <button className="ev-btn ev-btn-add" title="Add Seats" onClick={()=>handleAddSeats(h.id)}>
                            <i className="bi bi-plus-lg"/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
